import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  FiBarChart2, 
  FiDownload, 
  FiCalendar, 
  FiTrendingUp, 
  FiCheckCircle, 
  FiPieChart,
  FiFileText,
  FiFilter,
  FiSearch,
  FiRefreshCw,
  FiPrinter,
  FiInbox
} from "react-icons/fi";
import { rcaApi } from "../../../services/rcaApi.js";
import type { RcaRecordDto, RcaMethodCode } from "../../../../types/rca.types.js";

export function RcaReportsPage() {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedMethod, setSelectedMethod] = useState<string>("ALL");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL");
  const [selectedSeverity, setSelectedSeverity] = useState<string>("ALL");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // Fetch live RCA records from database
  const rcaQuery = useQuery({
    queryKey: ["cmts", "rca", "queue", "reports"],
    queryFn: async () => {
      const data = await rcaApi.queue();
      return data;
    },
    retry: 1,
  });

  const allRecords: readonly RcaRecordDto[] = rcaQuery.data || [];

  // Filter records based on selected criteria
  const filteredRecords = useMemo(() => {
    return allRecords.filter(r => {
      // Search term
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const matchesCode = r.rcaCode?.toLowerCase().includes(query);
        const matchesComplaint = r.complaintCode?.toLowerCase().includes(query) || r.complaintTitle?.toLowerCase().includes(query);
        const matchesProblem = r.problemStatement?.toLowerCase().includes(query);
        const matchesRootCause = r.rootCause?.toLowerCase().includes(query);
        const matchesOwner = r.ownerName?.toLowerCase().includes(query);
        if (!matchesCode && !matchesComplaint && !matchesProblem && !matchesRootCause && !matchesOwner) {
          return false;
        }
      }

      // Method filter
      if (selectedMethod !== "ALL") {
        if (r.rcaMethodCode !== selectedMethod && !r.rcaMethodName?.toUpperCase().includes(selectedMethod)) {
          return false;
        }
      }

      // Status filter
      if (selectedStatus !== "ALL") {
        if (r.statusCode !== selectedStatus && r.statusName?.toUpperCase() !== selectedStatus) {
          return false;
        }
      }

      // Severity filter
      if (selectedSeverity !== "ALL") {
        if (r.severity !== selectedSeverity) {
          return false;
        }
      }

      // Date range filter
      if (dateFrom) {
        const recordDate = new Date(r.createdAt || 0);
        const fromDate = new Date(dateFrom);
        if (recordDate < fromDate) return false;
      }

      if (dateTo) {
        const recordDate = new Date(r.createdAt || 0);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (recordDate > toDate) return false;
      }

      return true;
    });
  }, [allRecords, searchTerm, selectedMethod, selectedStatus, selectedSeverity, dateFrom, dateTo]);

  // Dynamic Statistics from filtered data
  const stats = useMemo(() => {
    const total = filteredRecords.length;
    const completed = filteredRecords.filter(r => r.statusCode === "RCA_COMPLETED" || r.statusName === "Completed").length;
    const inProgress = filteredRecords.filter(r => r.statusCode === "RCA_IN_PROGRESS" || !r.statusCode).length;
    const underReview = filteredRecords.filter(r => r.statusCode === "RCA_UNDER_REVIEW" || r.statusName === "Review").length;
    const overdue = filteredRecords.filter(r => r.isOverdue || r.statusCode === "RCA_OVERDUE").length;
    const verifiedRootCauses = filteredRecords.filter(r => r.rootCauseVerified || Boolean(r.rootCause)).length;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
    const verificationRate = total > 0 ? Math.round((verifiedRootCauses / total) * 100) : 0;

    return {
      total,
      completed,
      inProgress,
      underReview,
      overdue,
      verifiedRootCauses,
      completionRate,
      verificationRate,
    };
  }, [filteredRecords]);

  // CSV Download Handler
  const handleDownloadCsv = () => {
    if (filteredRecords.length === 0) {
      alert("No matching records found to download. Adjust your filter criteria.");
      return;
    }

    setIsExporting(true);
    try {
      const headers = [
        "RCA Code",
        "Complaint Code",
        "Complaint Title",
        "RCA Method",
        "Problem Statement",
        "Root Cause",
        "Root Cause Category",
        "Verified",
        "Owner",
        "Target Date",
        "Status",
        "Severity",
        "Priority",
        "SLA Status",
        "Created Date"
      ];

      const rows = filteredRecords.map(r => [
        `"${(r.rcaCode || "").replace(/"/g, '""')}"`,
        `"${(r.complaintCode || "").replace(/"/g, '""')}"`,
        `"${(r.complaintTitle || "").replace(/"/g, '""')}"`,
        `"${(r.rcaMethodName || r.rcaMethodCode || "").replace(/"/g, '""')}"`,
        `"${(r.problemStatement || "").replace(/"/g, '""')}"`,
        `"${(r.rootCause || "Under Investigation").replace(/"/g, '""')}"`,
        `"${(r.rootCauseCategoryName || r.rootCauseCategoryCode || "-").replace(/"/g, '""')}"`,
        r.rootCauseVerified ? "YES" : "NO",
        `"${(r.ownerName || "Unassigned").replace(/"/g, '""')}"`,
        `"${(r.targetDate ? r.targetDate.slice(0, 10) : "-")}"`,
        `"${(r.statusName || r.statusCode || "In Progress").replace(/"/g, '""')}"`,
        `"${(r.severity || "HIGH")}"`,
        `"${(r.priorityLevel || "HIGH")}"`,
        `"${(r.slaDaysText || (r.isOverdue ? "Overdue" : "On Track"))}"`,
        `"${(r.createdAt ? r.createdAt.slice(0, 10) : "-")}"`
      ]);

      const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(row => row.join(","))].join("\r\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute("href", url);
      link.setAttribute("download", `RCA_Investigation_Report_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  // Print / PDF Handler
  const handlePrintReport = () => {
    window.print();
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedMethod("ALL");
    setSelectedStatus("ALL");
    setSelectedSeverity("ALL");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">CMTS &gt; Analytics & Audit Reports</div>
          <h1 className="text-2xl font-bold text-slate-900 mt-1">RCA Analytics & Report Generator</h1>
          <p className="text-xs text-slate-500 mt-1">
            Filter, analyze, and export real-time RCA investigation records, root-cause trends, and compliance metrics directly from the database.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={handlePrintReport}
            className="px-4 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
          >
            <FiPrinter className="w-4 h-4" />
            Print / PDF
          </button>

          <button
            type="button"
            onClick={handleDownloadCsv}
            disabled={isExporting || filteredRecords.length === 0}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-2 transition-all transform hover:-translate-y-0.5"
          >
            <FiDownload className="w-4 h-4" />
            {isExporting ? "Generating CSV..." : `Download CSV (${filteredRecords.length})`}
          </button>
        </div>
      </div>

      {/* KPI Overview of Filtered Records */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Filtered Records</span>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</div>
          <span className="text-[11px] text-slate-500 mt-1 block">Total matching criteria</span>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Root Cause Verified</span>
          <div className="text-2xl font-bold text-emerald-600 mt-1">{stats.verifiedRootCauses}</div>
          <span className="text-[11px] text-emerald-600 font-semibold mt-1 block">{stats.verificationRate}% Verification Rate</span>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">In Progress / Review</span>
          <div className="text-2xl font-bold text-sky-700 mt-1">{stats.inProgress + stats.underReview}</div>
          <span className="text-[11px] text-slate-500 mt-1 block">{stats.inProgress} Active · {stats.underReview} Review</span>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Overdue Escalations</span>
          <div className="text-2xl font-bold text-rose-700 mt-1">{stats.overdue}</div>
          <span className="text-[11px] text-rose-600 font-semibold mt-1 block">
            {stats.overdue > 0 ? "Requires quality review" : "All SLAs on track"}
          </span>
        </div>
      </div>

      {/* Comprehensive Filter Bar */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FiFilter className="w-4 h-4 text-blue-600" />
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Report Filter Criteria</h3>
          </div>

          <button
            type="button"
            onClick={handleResetFilters}
            className="text-xs text-slate-500 hover:text-blue-600 font-semibold flex items-center gap-1 transition-colors"
          >
            <FiRefreshCw className="w-3.5 h-3.5" /> Reset Filters
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Keyword Search */}
          <div className="lg:col-span-2">
            <label className="block text-[11px] font-bold text-slate-600 mb-1">Search Keywords</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-2.5 text-slate-400 w-3.5 h-3.5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="RCA code, complaint, root cause, owner..."
                className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Method Filter */}
          <div>
            <label className="block text-[11px] font-bold text-slate-600 mb-1">RCA Method</label>
            <select
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
              className="w-full py-1.5 px-3 text-xs rounded-lg border border-slate-300 bg-white text-slate-700 font-medium focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Methods</option>
              <option value="FIVE_WHY">5-Why Analysis</option>
              <option value="FISHBONE">Fishbone (Ishikawa)</option>
              <option value="8D">8D Problem Solving</option>
              <option value="FTA">Fault Tree Analysis</option>
              <option value="PARETO">Pareto Analysis</option>
              <option value="PROCESS_MAP">Process Mapping</option>
              <option value="IS_IS_NOT">Is / Is Not Analysis</option>
              <option value="BRAINSTORMING">Brainstorming</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-[11px] font-bold text-slate-600 mb-1">Investigation Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full py-1.5 px-3 text-xs rounded-lg border border-slate-300 bg-white text-slate-700 font-medium focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Statuses</option>
              <option value="RCA_INITIATED">Initiated / Active</option>
              <option value="RCA_IN_PROGRESS">In Progress</option>
              <option value="RCA_UNDER_REVIEW">Under Review</option>
              <option value="RCA_COMPLETED">Completed</option>
              <option value="RCA_OVERDUE">Overdue</option>
            </select>
          </div>

          {/* From Date */}
          <div>
            <label className="block text-[11px] font-bold text-slate-600 mb-1">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full py-1.5 px-2 text-xs rounded-lg border border-slate-300 bg-white text-slate-700 font-medium focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* To Date */}
          <div>
            <label className="block text-[11px] font-bold text-slate-600 mb-1">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full py-1.5 px-2 text-xs rounded-lg border border-slate-300 bg-white text-slate-700 font-medium focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Live Data Preview Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden space-y-3">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Database Records Preview</h3>
            <p className="text-xs text-slate-500">Showing {filteredRecords.length} of {allRecords.length} records in database</p>
          </div>

          <button
            type="button"
            onClick={handleDownloadCsv}
            disabled={filteredRecords.length === 0}
            className="px-3.5 py-1.5 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 text-blue-700 font-semibold text-xs rounded-lg border border-blue-200 flex items-center gap-1.5 transition-colors"
          >
            <FiDownload className="w-3.5 h-3.5" /> Export Filtered Results
          </button>
        </div>

        {filteredRecords.length === 0 ? (
          <div className="p-12 text-center space-y-2">
            <FiInbox className="w-10 h-10 text-slate-300 mx-auto" />
            <h4 className="text-sm font-bold text-slate-700">No Records Match Current Filters</h4>
            <p className="text-xs text-slate-400">Try changing your search terms or resetting the filter options above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 uppercase text-[11px]">
                <tr>
                  <th className="py-3 px-4">RCA Code</th>
                  <th className="py-3 px-3">Complaint</th>
                  <th className="py-3 px-3">Method</th>
                  <th className="py-3 px-4 max-w-xs">Verified Root Cause / Problem Statement</th>
                  <th className="py-3 px-3">Category</th>
                  <th className="py-3 px-3">Owner</th>
                  <th className="py-3 px-3">Target Date</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3">SLA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRecords.map(record => (
                  <tr key={record.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3.5 px-4 font-bold text-blue-600 whitespace-nowrap">
                      {record.rcaCode}
                    </td>
                    <td className="py-3.5 px-3 font-medium text-slate-800 whitespace-nowrap">
                      <div>{record.complaintCode || record.complaintId}</div>
                      {record.complaintTitle && (
                        <div className="text-[11px] text-slate-500 max-w-[140px] truncate">{record.complaintTitle}</div>
                      )}
                    </td>
                    <td className="py-3.5 px-3 font-semibold text-slate-700 whitespace-nowrap">
                      {record.rcaMethodName || record.rcaMethodCode || "5-Why"}
                    </td>
                    <td className="py-3.5 px-4 text-slate-800 max-w-sm">
                      <div className="font-semibold text-slate-900 truncate">
                        {record.rootCause || "Investigation In Progress"}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {record.problemStatement}
                      </div>
                    </td>
                    <td className="py-3.5 px-3">
                      <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-semibold text-[11px]">
                        {record.rootCauseCategoryName || record.rootCauseCategoryCode || "METHOD"}
                      </span>
                    </td>
                    <td className="py-3.5 px-3 text-slate-600 font-medium whitespace-nowrap">
                      {record.ownerName || "Unassigned"}
                    </td>
                    <td className="py-3.5 px-3 text-slate-600 whitespace-nowrap">
                      {record.targetDate ? record.targetDate.slice(0, 10) : "-"}
                    </td>
                    <td className="py-3.5 px-3 whitespace-nowrap">
                      <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-sky-50 text-sky-700 border border-sky-200">
                        {record.statusName || record.statusCode || "In Progress"}
                      </span>
                    </td>
                    <td className="py-3.5 px-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${
                        record.isOverdue 
                          ? "bg-rose-50 text-rose-700 border-rose-200" 
                          : "bg-emerald-50 text-emerald-700 border-emerald-200"
                      }`}>
                        {record.slaDaysText || (record.isOverdue ? "Overdue" : "On Track")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
